/**
 * @contract
 *   Назначение: Инлайн-форма отправки отзыва о ЧОПе на публичной странице /chop/[slug]
 *   Поля:       Рейтинг звёздами (1-5), текст отзыва (20-2000 символов), имя (опционально)
 *   Антиспам:   Honeypot-поле, скрытое от пользователей
 *   Статус:     После submit — «Спасибо, отзыв отправлен на модерацию»
 *   Автор:      AI Agent
 *   Дата:       2026-06-23
 */

"use client";

import React, { useState } from "react";
import { Star, Send, CheckCircle, AlertCircle, MessageSquare } from "lucide-react";
import { Button } from "@oxpa/shared/ui/button";
import { useReviewSubmit, type SubmitState } from "@/features/seo-portal/hooks/use-review-submit";

interface ChopReviewFormProps {
  companyId: string;
}

/**
 * Инлайн-форма отзыва на странице ЧОПа.
 * Пользователь может оставить отзыв без авторизации. После отправки — сообщение об успехе.
 * Использует общий хук useReviewSubmit (единый API-контракт с add-review-dialog.tsx).
 */
export function ChopReviewForm({ companyId }: ChopReviewFormProps) {
  const { state, errorMessage, submit, reset: resetHook } = useReviewSubmit();

  // Поля формы
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [content, setContent] = useState("");
  const [authorName, setAuthorName] = useState("");

  // Honeypot — скрытое поле, которое заполняют только боты
  const [honeypot, setHoneypot] = useState("");

  // Валидация: оценка обязательна, текст минимум 20 символов (проверяется хуком)
  const isValid = rating > 0 && content.trim().length >= 20;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;

    await submit({
      chop_id: companyId,
      author_name: authorName.trim() || "Аноним",
      author_role: "client",
      rating,
      content: content.trim(),
      honeypot,
    });
  };

  // Сброс формы для повторной отправки
  const handleReset = () => {
    resetHook();
    setRating(0);
    setHoverRating(0);
    setContent("");
    setAuthorName("");
    setHoneypot("");
  };

  // Состояние: успешная отправка
  if (state === "success") {
    return (
      <div className="bg-black border border-green-500/30 rounded-sm p-6 md:p-8">
        <div className="flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
            <CheckCircle className="w-7 h-7 text-green-500" />
          </div>
          <div>
            <h3 className="font-black text-white uppercase tracking-tight mb-1 text-lg">
              Спасибо за ваш отзыв!
            </h3>
            <p className="text-white/50 text-sm font-sans max-w-md mx-auto">
              Отзыв отправлен на модерацию и появится на сайте после проверки
              (обычно в течение 24 часов).
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="border-white/20 hover:bg-white/10 text-white rounded-sm"
          >
            Написать ещё отзыв
          </Button>
        </div>
      </div>
    );
  }

  // Основная форма
  return (
    <div className="bg-black border border-white/20 rounded-sm p-6 md:p-8">
      <h3 className="font-black text-white uppercase tracking-tight mb-5 flex items-center gap-2 text-base">
        <MessageSquare className="w-5 h-5 text-white/50" />
        Оставить отзыв о компании
      </h3>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot — скрыт от людей, видим для ботов */}
        {/* ponytail: ступень 2. Стандартный антиспам-паттерн. */}
        <input
          type="text"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute opacity-0 pointer-events-none w-0 h-0"
        />

        {/* Рейтинг звёздами */}
        <div className="space-y-1.5">
          <label className="text-white/70 text-xs uppercase tracking-widest font-bold">
            Оценка <span className="text-white/50">*</span>
          </label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="p-1 rounded transition-transform hover:scale-110"
                aria-label={`Оценить на ${star} ${star === 1 ? "звезду" : star < 5 ? "звезды" : "звёзд"}`}
              >
                <Star
                  className={`w-8 h-8 transition-colors ${
                    star <= (hoverRating || rating)
                      ? "fill-orange-400 text-amber-600"
                      : "fill-transparent text-white/20"
                  }`}
                />
              </button>
            ))}
            {rating > 0 && (
              <span className="ml-3 text-sm text-white/50 font-sans">
                {["", "Плохо", "Так себе", "Нормально", "Хорошо", "Отлично"][rating]}
              </span>
            )}
          </div>
        </div>

        {/* Текст отзыва */}
        <div className="space-y-1.5">
          <label className="text-white/70 text-xs uppercase tracking-widest font-bold">
            Текст отзыва <span className="text-white/50">*</span>
            <span className="ml-2 text-white/30 normal-case font-normal">
              (мин. 20 символов)
            </span>
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Поделитесь опытом работы или услугами этого ЧОПа..."
            required
            minLength={20}
            maxLength={2000}
            rows={4}
            className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/30 rounded-sm p-3 text-sm font-sans resize-none focus:outline-none focus:ring-1 focus:ring-white/40 transition-colors"
          />
          <div className="flex justify-between items-center">
            {content.length > 0 && content.length < 20 && (
              <span className="text-[11px] text-amber-400 font-sans">
                Минимум 20 символов (осталось {20 - content.length})
              </span>
            )}
            <span className="text-[11px] text-white/30 font-sans ml-auto">
              {content.length}/2000
            </span>
          </div>
        </div>

        {/* Имя (необязательное) */}
        <div className="space-y-1.5">
          <label className="text-white/70 text-xs uppercase tracking-widest font-bold">
            Ваше имя <span className="text-white/30 font-normal normal-case">(необязательно)</span>
          </label>
          <input
            type="text"
            value={authorName}
            onChange={(e) => setAuthorName(e.target.value)}
            placeholder="Аноним"
            maxLength={100}
            className="w-full bg-white/5 border border-white/20 text-white placeholder:text-white/30 rounded-sm p-2.5 text-sm font-sans focus:outline-none focus:ring-1 focus:ring-white/40 transition-colors"
          />
        </div>

        {/* Дисклеймер */}
        <p className="text-[10px] text-white/30 font-sans text-center uppercase tracking-widest leading-relaxed">
          Отзыв пройдёт модерацию перед публикацией. Мы не публикуем
          оскорбления, спам и личные данные.
        </p>

        {/* Ошибка */}
        {state === "error" && errorMessage && (
          <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm">
            <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-red-400 text-sm font-sans">{errorMessage}</p>
          </div>
        )}

        {/* Кнопка отправки */}
        <Button
          type="submit"
          disabled={state === "submitting" || !isValid}
          className="w-full bg-white text-black hover:bg-white/90 rounded-sm font-black uppercase tracking-wider text-xs gap-2 h-11 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {state === "submitting" ? "Отправляем..." : "Отправить отзыв"}
        </Button>
      </form>
    </div>
  );
}
